/**
 * Voice button component — large circular push-to-talk microphone button.
 * 80px diameter, accessible, with visual state feedback.
 *
 * States:
 *   idle       — soft muted background, mic icon
 *   listening  — pulsing teal, animated rings
 *   processing — spinning indicator
 *   error      — soft coral background
 */

import { t } from '../../i18n';

export type VoiceButtonState = 'idle' | 'listening' | 'processing' | 'error';

interface VoiceButtonElements {
  root: HTMLElement;
  setState: (state: VoiceButtonState) => void;
  getState: () => VoiceButtonState;
}

/**
 * Creates a push-to-talk voice button.
 * Hold (pointerdown) to start listening, release (pointerup) to process.
 */
export function createVoiceButton(
  onPress: () => void,
  onRelease: () => void,
): VoiceButtonElements {
  let currentState: VoiceButtonState = 'idle';

  // -- Root wrapper with pulse rings ---
  const root = document.createElement('div');
  root.className = 'voice-indicator';
  root.style.cssText = 'position: relative; display: inline-flex; align-items: center; justify-content: center;';

  // Pulse ring (shown during listening)
  const ring1 = document.createElement('div');
  ring1.className = 'voice-indicator-ring';
  ring1.setAttribute('aria-hidden', 'true');
  root.appendChild(ring1);

  const ring2 = document.createElement('div');
  ring2.className = 'voice-indicator-ring';
  ring2.setAttribute('aria-hidden', 'true');
  ring2.style.animationDelay = '0.4s';
  root.appendChild(ring2);

  // -- Button ---
  const btn = document.createElement('button');
  btn.setAttribute('role', 'button');
  btn.setAttribute('aria-label', t('voice.holdToSpeak'));
  btn.setAttribute('aria-live', 'polite');
  btn.style.cssText = `
    position: relative;
    z-index: 1;
    width: 80px;
    height: 80px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    line-height: 1;
    transition: background-color 200ms ease, transform 150ms ease;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
    background-color: var(--color-muted);
    color: #ffffff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  `;

  // Microphone icon as Unicode
  const micIcon = document.createElement('span');
  micIcon.setAttribute('aria-hidden', 'true');
  micIcon.textContent = '\uD83C\uDF99'; // studio microphone emoji
  micIcon.style.cssText = 'font-size: 32px; line-height: 1;';
  btn.appendChild(micIcon);

  // Spinner (hidden by default, shown during processing)
  const spinner = document.createElement('span');
  spinner.setAttribute('aria-hidden', 'true');
  spinner.style.cssText = `
    display: none;
    width: 28px;
    height: 28px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 9999px;
    animation: voice-btn-spin 0.7s linear infinite;
  `;
  btn.appendChild(spinner);

  // Inject keyframe for spinner
  if (!document.getElementById('voice-btn-spin-style')) {
    const style = document.createElement('style');
    style.id = 'voice-btn-spin-style';
    style.textContent = `
      @keyframes voice-btn-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  root.appendChild(btn);

  // -- State management ---
  function applyState(state: VoiceButtonState): void {
    currentState = state;

    // Reset
    micIcon.style.display = 'inline';
    spinner.style.display = 'none';
    root.classList.remove('listening');
    btn.style.transform = '';

    switch (state) {
      case 'idle':
        btn.style.backgroundColor = 'var(--color-muted)';
        btn.setAttribute('aria-label', t('voice.holdToSpeak'));
        break;
      case 'listening':
        btn.style.backgroundColor = 'var(--color-primary)';
        btn.style.transform = 'scale(1.1)';
        btn.setAttribute('aria-label', t('voice.listening'));
        root.classList.add('listening');
        break;
      case 'processing':
        btn.style.backgroundColor = 'var(--color-primary)';
        btn.setAttribute('aria-label', t('voice.processing'));
        micIcon.style.display = 'none';
        spinner.style.display = 'inline-block';
        break;
      case 'error':
        btn.style.backgroundColor = 'var(--color-danger)';
        btn.setAttribute('aria-label', t('voice.errorTryAgain'));
        break;
    }
  }

  // -- Event handlers (push-to-talk) ---
  let pressing = false;

  function handlePress(e: Event): void {
    e.preventDefault();
    if (currentState === 'processing') return;
    pressing = true;
    onPress();
  }

  function handleRelease(e: Event): void {
    e.preventDefault();
    if (!pressing) return;
    pressing = false;
    onRelease();
  }

  // Pointer events for unified touch + mouse
  btn.addEventListener('pointerdown', handlePress);
  btn.addEventListener('pointerup', handleRelease);
  btn.addEventListener('pointercancel', handleRelease);
  btn.addEventListener('pointerleave', (e) => {
    if (pressing) handleRelease(e);
  });

  // Keyboard support: Space / Enter to toggle
  btn.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (!pressing) {
        handlePress(e);
      }
    }
  });

  btn.addEventListener('keyup', (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (pressing) {
        handleRelease(e);
      }
    }
  });

  // Context menu prevention on long-press (mobile)
  btn.addEventListener('contextmenu', (e) => e.preventDefault());

  applyState('idle');

  return {
    root,
    setState: applyState,
    getState: () => currentState,
  };
}
